import React, { useEffect, useState } from "react";
import {
  Drawer,
  Row,
  Col,
  Button,
  Divider,
  Checkbox,
  notification,
} from "antd";
import textStyles from "../../Shared/Styles/TextStyles.module.scss";
import dividerStyles from "../../Shared/Styles/DividerStyles.module.scss";
import drawerStyles from "../../DashBoard/TripDetails/TripDetailsDrawer.module.scss";
import styles from "./ShoppingCart.module.scss";
import buttonStyles from "../../Shared/Styles/ButtonStyles.module.scss";
import deleteRedIcon from "../../../assets/icons/deleteRedFilled.svg";
import deleteIcon from "../../../assets/icons/deleteFilled.svg";
import shoppingCart from "../../../assets/images/shoppingCart.svg";
import GroceryCartItemView from "./GroceryCartItemView";
import EmptyShoppingCartModal from "../Modals/EmptyShoppingCartModal";
import ShoppingCartDeliveryInstructionsModal from "../Modals/ShoppingCartDeliveryInstructionsModal";
import {
  CartDeleteAction,
  ShoppingCartDetails,
} from "../../../types/GroceryTypes";
import { useParams } from "react-router";
import { useTripsCtx } from "../../../context/TripDetailsContext";
import { useGroceryCtx } from "../../../context/GroceryContext";
import GroceryCartCostDetails from "./GroceryCartCostDetails";
import GoogleLoader from "../../../assets/Loader/GoogleSvgLoader/GoogleLoader";
import { deleteTempGroceryCart } from "../../../services/GroceryService";

type Props = {
  isDrawerVisible: boolean;
  setIsDrawerVisible: Function;
  isShoppingCartLoading: boolean;
  shoppingCartDetails: ShoppingCartDetails;
  fetchShoppingCartInformation: Function;
};

function ShoppingCartDrawer({
  isDrawerVisible,
  setIsDrawerVisible,
  isShoppingCartLoading,
  shoppingCartDetails,
  fetchShoppingCartInformation,
}: Props) {
  const { deliveryNoteCtx, cachedGroceryCartItemsCtx } = useGroceryCtx();
  const [deliveryNote, setDeliveryNote] = deliveryNoteCtx;
  const [cachedGroceryCartItems] = cachedGroceryCartItemsCtx;

  const [allowSubstitutions, setAllowSubstitutions] = useState<boolean>(true);
  const [isInstructionsModalVisible, setIsInstructionsModalVisible] = useState<
    boolean
  >(false);
  const [isEmptyCartModalVisible, setisEmptyCartModalVisible] = useState<
    boolean
  >(false);

  const { serviceDataCtx } = useTripsCtx();
  const [serviceData] = serviceDataCtx;

  const { personId } = useParams<{ personId: string }>();
  const serviceUserFirstName = serviceData?.find(
    (x: { personId: number }) => x.personId === Number(personId)
  )?.firstName;

  const handleDrawerClose = () => {
    setIsDrawerVisible(false);
  };
  const handleAllowSubstitutions = () =>
    setAllowSubstitutions(!allowSubstitutions);

  function handleShoppingCartCheckout() {
    setIsDrawerVisible(false);
  }

  async function handleClearDeliveryInstructions() {
    try {
      await deleteTempGroceryCart(
        Number(personId),
        CartDeleteAction.DeleteDeliveryNote
      );
      setDeliveryNote("");
    } catch {
      notification.error({
        message: "Failed to delete delivery instructions.",
      });
    }
  }

  useEffect(() => {
    fetchShoppingCartInformation();
  }, [cachedGroceryCartItems]);

  return (
    <>
      <Drawer
        title={
          <Row
            className={`${textStyles.normalText20} ${styles.shoppingCartHeader}`}
          >
            Shopping Cart
          </Row>
        }
        placement="right"
        visible={isDrawerVisible}
        closable={true}
        onClose={handleDrawerClose}
        className={`${drawerStyles.drawerContainer} ${styles.shoppingCartDrawer} `}
        maskClosable={false}
        footer={
          <Button
            className={`${styles.startCheckoutBtn} ${buttonStyles.primaryBlueBtn} `}
            onClick={handleShoppingCartCheckout}
            disabled={isShoppingCartLoading}
          >
            {shoppingCartDetails.groceryItems?.length > 0
              ? "Start checkout"
              : "Start shopping"}
          </Button>
        }
      >
        {isShoppingCartLoading ? (
          <GoogleLoader />
        ) : (
          <>
            <Divider className={dividerStyles.solidBrandingDivider} />
            {shoppingCartDetails.groceryItems?.length > 0 ? (
              <Row className={styles.shoppingCartBody}>
                <Col span={24} className={styles.substitutionsOptionField}>
                  <Checkbox
                    onChange={handleAllowSubstitutions}
                    checked={allowSubstitutions}
                    className={styles.substitutionsCheckbox}
                  >
                    Allow all substitutions
                  </Checkbox>
                </Col>
                <Col span={24}>
                  {shoppingCartDetails.groceryItems.map(
                    (groceryCategoryItem) => (
                      <>
                        <div
                          className={`${textStyles.normalText20} ${styles.groceryCategoryName}`}
                        >
                          {groceryCategoryItem.categoryName}
                        </div>
                        {groceryCategoryItem.groceryItems.map((groceryItem) => (
                          <GroceryCartItemView
                            shoppingCartGroceryItems={groceryItem}
                          />
                        ))}
                      </>
                    )
                  )}
                </Col>
                <Col span={24}>
                  <Row className={styles.deliveryInstructionOption}>
                    <div className={textStyles.normalText20}>
                      Delivery instructions
                    </div>
                    <button
                      className={styles.addNoteBtn}
                      onClick={() => {
                        setIsInstructionsModalVisible(true);
                      }}
                    >
                      Add note
                    </button>
                  </Row>
                </Col>
                {deliveryNote?.length > 0 && (
                  <Col span={24}>
                    <Row className={styles.deliveryInstructions}>
                      <Col span={22} className={textStyles.normalText14}>
                        {deliveryNote}
                      </Col>
                      <Col span={2} className={styles.alignTextRight}>
                        <Button
                          className={styles.deleteDeliveryNoteBtn}
                          onClick={handleClearDeliveryInstructions}
                        >
                          <img src={deleteIcon} alt="delete_delivery_notes" />
                        </Button>
                      </Col>
                    </Row>
                  </Col>
                )}
                <Divider className={dividerStyles.solidFadedDividerMargin0} />
                <Divider className={dividerStyles.solidFadedDivider} />
                <Col span={24}>
                  <GroceryCartCostDetails
                    shoppingCartCostDetails={shoppingCartDetails.costDetails}
                  />
                </Col>
                <Col span={24} className={styles.shoppingCartInfo}>
                  Online and in-store prices may vary. Your estimated total may
                  change to reflect substitutions and final item weights.
                </Col>
                <Col span={24}>
                  <Row className={styles.emptyCartOption}>
                    <Button
                      className={`${styles.emptyCartBtn} ${buttonStyles.deleteRedBtn16} `}
                      onClick={() => {
                        setisEmptyCartModalVisible(true);
                      }}
                    >
                      <img src={deleteRedIcon} alt="empty_shopping_cart" />
                      Empty cart
                    </Button>
                  </Row>
                </Col>
              </Row>
            ) : (
              <div className={styles.emptyShoppingCart}>
                <Row justify="center">
                  <img src={shoppingCart} alt="shopping_cart" />
                </Row>
                <Row
                  justify="center"
                  className={`${styles.emptyShoppingCartInfo} ${textStyles.normalText14}`}
                >
                  <p>{serviceUserFirstName}’s cart is empty.</p>
                  <p>Once you add items, we’ll keep track of them here.</p>
                </Row>
              </div>
            )}
          </>
        )}
      </Drawer>

      {isEmptyCartModalVisible && (
        <EmptyShoppingCartModal
          isVisible={isEmptyCartModalVisible}
          setVisibility={setisEmptyCartModalVisible}
          serviceUserPersonId={Number(personId)}
          handleDrawerClose={handleDrawerClose}
        />
      )}
      {isInstructionsModalVisible && (
        <ShoppingCartDeliveryInstructionsModal
          isVisible={isInstructionsModalVisible}
          setVisibility={setIsInstructionsModalVisible}
          serviceUserPersonId={Number(personId)}
        />
      )}
    </>
  );
}

export default ShoppingCartDrawer;
